import { Check, Star } from 'lucide-react';
import { TierInfo } from '../types';
import { FOUNDING_MEMBER_PRICE, ANNUAL_PRICES } from '../constants';

interface TierCardProps {
  tier: TierInfo;
  interval: 'monthly' | 'annual';
  onSelect: (tierId: string) => void;
}

export default function TierCard({ tier, interval, onSelect }: TierCardProps) {
  const isIron = tier.id === 'iron';
  const isForce = tier.id === 'force';
  const isSummit = tier.id === 'summit';
  const comingSoon = isForce || isSummit;
  const annual = ANNUAL_PRICES[tier.id];
  const isAnnual = interval === 'annual';

  const hasFoundingPrice = !!tier.foundingPrice;
  // Calculate display prices
  const monthlyPrice = hasFoundingPrice ? tier.foundingPrice! : tier.price;
  const annualTotal = hasFoundingPrice && annual?.foundingAnnual ? annual.foundingAnnual : annual?.annual || 0;
  const annualPerMonth = Math.round(annualTotal / 12);
  const displayPrice = isAnnual ? annualPerMonth : monthlyPrice;
  const standardAnnualTotal = annual?.annual || 0;
  const standardAnnualPerMonth = Math.round(standardAnnualTotal / 12);

  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-4 md:p-6 transition-all duration-300 hover:translate-y-[-2px] ${
        tier.highlighted
          ? 'border-blue-500/50 bg-blue-500/10 shadow-lg shadow-blue-500/10 md:scale-[1.02]'
          : 'border-white/10 bg-white/5 hover:border-white/20'
      }`}
    >
      {/* Founding member badge */}
      {hasFoundingPrice && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500 text-white text-[10px] font-bold shadow-lg whitespace-nowrap">
          <Star className="w-3 h-3 fill-current" />
          50% OFF — LOCKED FOR LIFE
        </div>
      )}

      <div className="mb-3 md:mb-4">
        <h3 className="text-lg md:text-xl font-extrabold mb-0.5">{tier.name}</h3>
        <p className="text-white/50 text-[11px] md:text-xs">{tier.tagline}</p>
      </div>

      {/* Price */}
      <div className="mb-3 md:mb-4">
        {hasFoundingPrice ? (
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl md:text-3xl font-extrabold text-blue-400">${displayPrice}</span>
              <span className="text-white/40 text-xs md:text-sm">/month</span>
            </div>
            {isAnnual ? (
              <p className="text-[11px] md:text-xs text-white/40 mt-0.5">
                <span className="text-blue-400">${annualTotal}/year</span>
                <span className="ml-1.5">Locked for life — first 10 only</span>
              </p>
            ) : (
              <p className="text-[11px] md:text-xs text-white/40 mt-0.5">
                <span className="line-through">${tier.price}/month</span>
                <span className="text-blue-400 ml-1.5">Locked for life — first 10 only</span>
              </p>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl md:text-3xl font-extrabold">${isAnnual ? standardAnnualPerMonth : tier.price}</span>
              <span className="text-white/40 text-xs md:text-sm">/month</span>
            </div>
            {isAnnual && (
              <p className="text-[11px] md:text-xs text-green-400 mt-0.5">
                ${standardAnnualTotal}/year — save ${tier.price * 2}/year
              </p>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="flex-1 space-y-1.5 md:space-y-2 mb-4 md:mb-5">
        {tier.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <span className="text-white/70 text-[11px] md:text-xs">{feature.title}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {comingSoon ? (
        <button
          disabled
          className="w-full py-2 md:py-2.5 rounded-xl border border-white/10 text-white/30 font-semibold text-xs cursor-not-allowed"
        >
          Coming Soon
        </button>
      ) : (
        <button
          onClick={() => onSelect(tier.id)}
          className="w-full py-2 md:py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 active:scale-[0.98] text-white font-bold text-[11px] md:text-xs transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
        >
          {hasFoundingPrice
            ? `Join as Founding Member — $${isAnnual ? annualTotal + '/yr' : monthlyPrice + '/mo'}`
            : `Get Started — $${isAnnual ? standardAnnualTotal + '/yr' : tier.price + '/mo'}`
          }
        </button>
      )}
    </div>
  );
}
